import { localizeDateNames } from "@/lib/dateNames";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLatest } from "@/hooks/useLatest";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
    Plus, Search, Star, Phone, Mail, Globe, MapPin, User,
    Pencil, Archive, ArchiveRestore, Power, Trash2, X,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const DELIVERY_STATUS_LABEL = {
    completed: "Completed", reviewed: "Reviewed",
    needs_review: "Needs Review", uploaded: "Draft",
};
const DELIVERY_STATUS_TONE = {
    completed: "completed", reviewed: "completed",
    needs_review: "action_required", uploaded: "pending",
};

const SUPPLIER_STATUS_LABEL = {
    active: "Active", inactive: "Inactive", archived: "Archived",
};
const SUPPLIER_STATUS_TONE = {
    active: "completed", inactive: "pending", archived: "action_required",
};

const DEFAULT_CATEGORIES = [
    "Fish", "Meat", "Vegetables", "Dairy", "Dry Goods", "Frozen",
    "Bakery", "Cleaning", "Packaging", "Wholesale", "Fish & Seafood", "Other",
];

const SORT_OPTIONS = [
    { value: "name", label: "Name (A → Z)", labelKey: "suppliers.nameAZ" },
    { value: "last_delivery", label: "Last delivery", labelKey: "suppliers.lastDelivery" },
    { value: "products", label: "Most products", labelKey: "suppliers.mostProducts" },
];

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return localizeDateNames(d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }));
}

export default function Suppliers() {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [editing, setEditing] = useState(null);

    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [statusFilter, setStatusFilter] = useState("");  // "", active, inactive, archived
    const [sort, setSort] = useState("name");

    const load = async () => {
        setLoading(true);
        const params = {};
        if (user.role === "corporate" && restaurantId) params.restaurant_id = restaurantId;
        if (search.trim()) params.q = search.trim();
        if (category) params.category = category;
        if (statusFilter) params.status = statusFilter;
        if (sort) params.sort = sort;
        try {
            const { data } = await api.get("/suppliers", { params });
            setSuppliers(data || []);
        } finally {
            setLoading(false);
        }
    };

    const loadRef = useLatest(load);
    useEffect(() => { loadRef.current(); }, [restaurantId, category, statusFilter, sort, loadRef]);
    useEffect(() => {
        const t = setTimeout(() => { loadRef.current(); }, 250);
        return () => clearTimeout(t);
    }, [search, loadRef]);

    const openDetail = async (id) => {
        try {
            const { data } = await api.get(`/suppliers/${id}`);
            setSelected(data);
        } catch {
            toast.error(t("suppliers.couldNotOpenSupplier", "Could not open supplier"));
        }
    };

    const refreshDetail = async (id) => {
        const { data } = await api.get(`/suppliers/${id}`);
        setSelected(data);
    };

    const categoriesInUse = useMemo(() => {
        const set = new Set(DEFAULT_CATEGORIES);
        suppliers.forEach((s) => { if (s.category) set.add(s.category); });
        return Array.from(set).sort();
    }, [suppliers]);

    const onCreated = () => { setAddOpen(false); load(); };
    const onUpdated = async () => {
        setEditing(null);
        await load();
        if (selected) await refreshDetail(selected.id);
    };

    return (
        <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
            <PageHeader
                title={t("suppliers.suppliers", "Suppliers")}
                subtitle={t("suppliers.whereDoWeBuyThisProduct", "Where do we buy this product?")}
                action={
                    <button
                        data-testid="add-supplier-button"
                        onClick={() => setAddOpen(true)}
                        className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                    >
                        <Plus className="h-4 w-4" /> {t("suppliers.newSupplier", "New Supplier")}
                    </button>
                }
            />

            {/* Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
                <div className="relative flex-1">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                        data-testid="supplier-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("suppliers.searchByNameCategoryEmailPhone", "Search by name, category, email, phone…")}
                        className="h-10 w-full pl-9 pr-3 border border-neutral-300 rounded-md text-sm focus:outline-none focus:border-neutral-900"
                    />
                </div>
                <select
                    data-testid="supplier-category-filter"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white focus:outline-none focus:border-neutral-900"
                >
                    <option value="">{t("suppliers.allCategories", "All categories")}</option>
                    {categoriesInUse.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
                <select
                    data-testid="supplier-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white focus:outline-none focus:border-neutral-900"
                >
                    <option value="">{t("suppliers.activeAndInactive", "Active & Inactive")}</option>
                    <option value="active">{t("common.active", "Active")}</option>
                    <option value="inactive">{t("common.inactive", "Inactive")}</option>
                    <option value="archived">{t("suppliers.archived", "Archived")}</option>
                </select>
                <select
                    data-testid="supplier-sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white focus:outline-none focus:border-neutral-900"
                >
                    {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{t(o.labelKey, o.label)}</option>
                    ))}
                </select>
            </div>

            {/* Empty state / list */}
            {loading ? (
                <div className="kop-card p-8 text-sm text-neutral-500" data-testid="suppliers-loading">
                    Loading suppliers…
                </div>
            ) : suppliers.length === 0 ? (
                <EmptyState
                    onAdd={() => setAddOpen(true)}
                    isFiltered={!!(search || category || statusFilter)}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {suppliers.map((s) => (
                        <SupplierCard key={s.id} supplier={s}
                            isCorporateUser={user.role === "corporate"}
                            onOpen={() => openDetail(s.id)} />
                    ))}
                </div>
            )}

            <AddSupplierDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={onCreated} />

            <SupplierDetailDialog
                supplier={selected}
                onClose={() => setSelected(null)}
                onEdit={() => setEditing(selected)}
                onChanged={() => refreshDetail(selected.id).then(load)}
                isCorporateUser={user.role === "corporate"}
            />

            <EditSupplierDialog
                supplier={editing}
                onClose={() => setEditing(null)}
                onSaved={onUpdated}
                isCorporateUser={user.role === "corporate"}
            />
        </div>
    );
}

function EmptyState({ onAdd, isFiltered }) {
    const { t } = useTranslation();
    if (isFiltered) {
        return (
            <div className="kop-card p-10 text-center" data-testid="suppliers-empty-filtered">
                <div className="font-display text-xl font-semibold mb-1">{t("suppliers.noMatchingSuppliers", "No matching suppliers.")}</div>
                <div className="text-sm text-neutral-500">{t("suppliers.tryRemovingSomeFiltersOrChange", "Try removing some filters or change the search.")}</div>
            </div>
        );
    }
    return (
        <div className="kop-card p-12 text-center" data-testid="suppliers-empty-state">
            <div className="font-display text-2xl font-bold mb-2">{t("suppliers.noSuppliersAvailableYet", "No suppliers available yet.")}</div>
            <div className="text-sm text-neutral-500 max-w-md mx-auto mb-6">
                {t("suppliers.suppliersWillAlsoBeCreatedAutomatically", "Suppliers will also be created automatically from completed deliveries.")}
            </div>
            <button
                data-testid="add-first-supplier-button"
                onClick={onAdd}
                className="h-11 px-5 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
            >
                <Plus className="h-4 w-4" /> {t("suppliers.addFirstSupplier", "Add First Supplier")}
            </button>
        </div>
    );
}

function SupplierCard({ supplier, isCorporateUser, onOpen }) {
    const { t } = useTranslation();
    const status = supplier.status || "active";
    return (
        <button
            data-testid={`supplier-card-${supplier.id}`}
            onClick={onOpen}
            className="kop-card p-5 text-left hover:border-neutral-400 transition-colors"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-display text-lg font-bold leading-tight">{supplier.name}</div>
                    {supplier.category && (
                        <div className="text-xs text-neutral-500 mt-1">{supplier.category}</div>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                    <span
                        data-testid={`supplier-visibility-${supplier.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${
                            supplier.is_recommended
                                ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                                : "bg-neutral-50 text-neutral-700 border-neutral-200"
                        }`}
                    >
                        {supplier.is_recommended ? t("suppliers.corporate", "Corporate") : t("suppliers.local", "Local")}
                    </span>
                    <StatusBadge status={SUPPLIER_STATUS_TONE[status] || "pending"}>
                        {t(`suppliers.status.${status}`, SUPPLIER_STATUS_LABEL[status] || status)}
                    </StatusBadge>
                </div>
            </div>

            <div className="text-sm text-neutral-700 mt-3 space-y-1">
                {supplier.phone && (
                    <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-neutral-400" />{supplier.phone}</div>
                )}
                {supplier.email && (
                    <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-neutral-400" />{supplier.email}</div>
                )}
            </div>

            <div className="mt-4 pt-3 border-t border-neutral-100 grid grid-cols-2 gap-2 text-xs text-neutral-500">
                <div>
                    <div className="uppercase tracking-wide text-[10px] font-semibold">{t("suppliers.lastDelivery", "Last delivery")}</div>
                    <div className="text-neutral-800 font-medium mt-0.5">{formatDate(supplier.last_delivery_date)}</div>
                </div>
                <div>
                    <div className="uppercase tracking-wide text-[10px] font-semibold">{t("suppliers.products", "Products")}</div>
                    <div className="text-neutral-800 font-medium mt-0.5">{supplier.products_count ?? 0}</div>
                </div>
                {isCorporateUser && (
                    <div className="col-span-2">
                        <div className="uppercase tracking-wide text-[10px] font-semibold">{t("common.restaurant", "Restaurant")}</div>
                        <div className="text-neutral-800 font-medium mt-0.5">
                            {supplier.restaurant_name || (supplier.is_recommended ? t("suppliers.companyWide", "Company-wide") : "—")}
                        </div>
                    </div>
                )}
            </div>
        </button>
    );
}

function SupplierDetailDialog({ supplier, onClose, onEdit, onChanged, isCorporateUser }) {
    const { t, i18n } = useTranslation();
    const [promoting, setPromoting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    if (!supplier) return null;
    const status = supplier.status || "active";
    const canEdit = isCorporateUser || !supplier.is_recommended;
    // Prompt 44 · Make Corporate — one-shot promotion, guarded by
    // backend duplicate-identity protection.
    const canPromote = isCorporateUser && !supplier.is_recommended;

    const toggleStatus = async (newStatus) => {
        try {
            await api.patch(`/suppliers/${supplier.id}`, { status: newStatus });
            toast.success(`Supplier ${newStatus === "archived" ? "archived" : newStatus === "active" ? "activated" : "set inactive"}`);
            onChanged();
        } catch {
            toast.error(t("suppliers.couldNotUpdateSupplier", "Could not update supplier"));
        }
    };

    // Prompt 44 · Make Corporate — one-shot promotion, guarded by
    // backend duplicate-identity protection. Chef-level users don't see
    // the button (canPromote gate); attempts by legacy chef tokens fail
    // with a friendly 403 message from the API.
    const makeCorporate = async () => {
        setPromoting(true);
        try {
            await api.post(`/suppliers/${supplier.id}/make-corporate`);
            toast.success(t("suppliers.supplierIsNowCorporateVisibleTo", "Supplier is now Corporate — visible to every restaurant in the company."));
            setShowConfirm(false);
            onChanged();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (e.response?.status === 409 && d && typeof d === "object") {
                toast.error(
                    i18n.language === "en"
                        ? `${d.message} Existing: ${d.conflict?.name || "another supplier"}.`
                        : t("suppliers.promoteConflict", { defaultValue: "A Corporate supplier with this name already exists: {{name}}.",
                            name: d.conflict?.name || t("suppliers.anotherSupplier", "another supplier") })
                );
            } else {
                toast.error(typeof d === "string" ? apiErrorMessage(e) : t("suppliers.couldNotPromoteSupplier", "Could not promote supplier."));
            }
        } finally { setPromoting(false); }
    };

    return (
        <Dialog open={!!supplier} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 flex-wrap">
                        {supplier.name}
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${
                                supplier.is_recommended
                                    ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                                    : "bg-neutral-50 text-neutral-700 border-neutral-200"
                            }`}
                        >
                            {supplier.is_recommended ? t("suppliers.corporate", "Corporate") : t("suppliers.local", "Local")}
                        </span>
                        <StatusBadge status={SUPPLIER_STATUS_TONE[status] || "pending"}>
                            {t(`suppliers.status.${status}`, SUPPLIER_STATUS_LABEL[status] || status)}
                        </StatusBadge>
                    </DialogTitle>
                    <DialogDescription>
                        {[supplier.category, supplier.restaurant_name].filter(Boolean).join(" · ")}
                    </DialogDescription>
                </DialogHeader>

                {/* Action row */}
                <div className="flex flex-wrap gap-2">
                    {canEdit && (
                        <button data-testid="edit-supplier-button" onClick={onEdit}
                            className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                            <Pencil className="h-4 w-4" /> {t("common.edit", "Edit")}
                        </button>
                    )}
                    {canPromote && (
                        <button data-testid="make-corporate-button"
                            onClick={() => setShowConfirm(true)}
                            className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-indigo-300 text-indigo-800 bg-indigo-50 text-sm font-semibold hover:bg-indigo-100">
                            <Star className="h-4 w-4" /> {t("suppliers.makeCorporate", "Make Corporate")}
                        </button>
                    )}
                    {canEdit && status === "active" && (
                        <button data-testid="set-inactive-button" onClick={() => toggleStatus("inactive")}
                            className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                            <Power className="h-4 w-4" /> {t("suppliers.setInactive", "Set inactive")}
                        </button>
                    )}
                    {canEdit && status === "inactive" && (
                        <button data-testid="set-active-button" onClick={() => toggleStatus("active")}
                            className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                            <Power className="h-4 w-4" /> {t("suppliers.setActive", "Set active")}
                        </button>
                    )}
                    {canEdit && status !== "archived" && (
                        <button data-testid="archive-supplier-button" onClick={() => toggleStatus("archived")}
                            className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50">
                            <Archive className="h-4 w-4" /> {t("common.archive", "Archive")}
                        </button>
                    )}
                    {canEdit && status === "archived" && (
                        <button data-testid="unarchive-supplier-button" onClick={() => toggleStatus("active")}
                            className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                            <ArchiveRestore className="h-4 w-4" /> {t("common.restore", "Restore")}
                        </button>
                    )}
                </div>

                {/* General Information */}
                <section className="mt-2">
                    <h4 className="font-semibold text-sm mb-3 uppercase tracking-wide text-neutral-500">{t("suppliers.generalInformation", "General information")}</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <InfoRow icon={Phone} label={t("common.phone", "Phone")} value={supplier.phone} />
                        <InfoRow icon={Mail} label={t("common.email", "Email")} value={supplier.email} />
                        <InfoRow icon={Globe} label={t("suppliers.website", "Website")} value={supplier.website} />
                        <InfoRow icon={MapPin} label={t("common.address", "Address")} value={supplier.address} />
                        <InfoRow icon={User} label={t("suppliers.contactPerson", "Contact person")} value={supplier.contact_person} />
                    </dl>
                    {supplier.notes && (
                        <div className="mt-3 text-sm text-neutral-600 whitespace-pre-line border-l-2 border-neutral-200 pl-3 italic">
                            {supplier.notes}
                        </div>
                    )}
                </section>

                {/* Last known prices */}
                {(supplier.products || []).length > 0 && (
                    <section className="mt-6">
                        <h4 className="font-semibold text-sm mb-2 uppercase tracking-wide text-neutral-500">
                            Last known prices ({supplier.products.length})
                        </h4>
                        <ul className="text-sm grid grid-cols-1 md:grid-cols-2 gap-1.5"
                            data-testid="supplier-products">
                            {supplier.products.slice(0, 30).map((p) => (
                                <li key={p.id} className="flex justify-between gap-2 px-3 py-2 bg-neutral-50 rounded">
                                    <span className="font-medium truncate">{p.product_name}</span>
                                    <span className="text-neutral-700 whitespace-nowrap font-mono">
                                        € {Number(p.last_price || 0).toFixed(2)} / {p.unit}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="text-[11px] text-neutral-500 italic mt-2">
                            {t("suppliers.readOnlyPricesAlwaysComeFrom", "Read-only. Prices always come from Deliveries.")}
                        </div>
                    </section>
                )}

                {/* Recommended Products (per-supplier operational helper) */}
                <RecommendedProductsSection supplier={supplier} />

                {/* Delivery History */}
                <section className="mt-6">
                    <h4 className="font-semibold text-sm mb-2 uppercase tracking-wide text-neutral-500">
                        Delivery history ({(supplier.deliveries || []).length})
                    </h4>
                    {(supplier.deliveries || []).length === 0 ? (
                        <div className="text-sm text-neutral-500 italic">{t("suppliers.noDeliveriesFromThisSupplierYet", "No deliveries from this supplier yet.")}</div>
                    ) : (
                        <div className="border border-neutral-200 rounded-md overflow-hidden">
                            <table className="w-full text-sm" data-testid="supplier-deliveries">
                                <thead className="bg-neutral-50">
                                    <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                        <th className="px-4 py-2 font-semibold">{t("suppliers.date", "Date")}</th>
                                        <th className="px-4 py-2 font-semibold">{t("common.status", "Status")}</th>
                                        <th className="px-4 py-2 font-semibold">{t("suppliers.items", "Items")}</th>
                                        <th className="px-4 py-2 font-semibold">{t("suppliers.total", "Total")}</th>
                                        {isCorporateUser && (
                                            <th className="px-4 py-2 font-semibold">{t("common.restaurant", "Restaurant")}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-100">
                                    {supplier.deliveries.map((d) => (
                                        <tr key={d.id}>
                                            <td className="px-4 py-2 font-medium">{formatDate(d.delivery_date)}</td>
                                            <td className="px-4 py-2">
                                                <StatusBadge status={DELIVERY_STATUS_TONE[d.status] || "pending"}>
                                                    {t(`deliveryStatus.${d.status}`, DELIVERY_STATUS_LABEL[d.status] || d.status)}
                                                </StatusBadge>
                                            </td>
                                            <td className="px-4 py-2 text-neutral-700">{d.items_count ?? 0}</td>
                                            <td className="px-4 py-2 text-neutral-700">€ {Number(d.total_amount || 0).toFixed(2)}</td>
                                            {isCorporateUser && (
                                                <td className="px-4 py-2 text-neutral-700">{d.restaurant_name || "—"}</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </DialogContent>
            {showConfirm && (
                <Dialog open onOpenChange={(v) => !v && setShowConfirm(false)}>
                    <DialogContent className="max-w-md"
                        data-testid="make-corporate-confirm">
                        <DialogHeader>
                            <DialogTitle>{t("suppliers.promoteConfirmTitle", { defaultValue: "Share {{name}} with all restaurants in this company?", name: supplier.name })}</DialogTitle>
                            <DialogDescription>
                                {t("suppliers.promoteConfirmBody", { defaultValue: "{{name}} becomes a Corporate supplier. Every restaurant in this company will be able to reference it in Deliveries and Products.", name: supplier.name })}
                                <br />
                                {t("suppliers.promoteScopeNote", "Existing deliveries, products, inventory and reports remain restaurant-scoped — sharing the supplier does not share operational data.")}
                                <br />
                                {t("suppliers.thisActionCannotBeUndoneFrom", "This action cannot be undone from the UI in this release.")}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <button
                                onClick={() => setShowConfirm(false)}
                                className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50">
                                {t("common.cancel", "Cancel")}
                            </button>
                            <button
                                data-testid="make-corporate-confirm-btn"
                                onClick={makeCorporate} disabled={promoting}
                                className="h-10 px-4 rounded-md bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40">
                                {promoting ? t("suppliers.promoting", "Promoting…") : t("suppliers.yesMakeCorporate", "Yes, make Corporate")}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </Dialog>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{label}</dt>
            <dd className="mt-0.5 flex items-center gap-1.5 text-neutral-800">
                <Icon className="h-3.5 w-3.5 text-neutral-400" />
                <span className="truncate">{value || "—"}</span>
            </dd>
        </div>
    );
}


// --------------------------------------------------------------------
// Recommended Products — per-supplier operational helper.
// PURELY manual list. Not tied to deliveries, purchasing, inventory,
// costs, VAT, or the Reports module. Visual reference only.
// --------------------------------------------------------------------
function RecommendedProductsSection({ supplier }) {
    const { t } = useTranslation();
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null | 'new' | product obj

    const supplierId = supplier?.id;
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/suppliers/${supplierId}/recommended-products`);
            setItems(data.products || []);
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("suppliers.failedToLoadRecommendedProducts", "Failed to load recommended products")));
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [supplierId]);

    useEffect(() => {
        if (supplier?.id) reload();
    }, [supplier?.id, reload]);

    async function handleDelete(product) {
        if (!window.confirm(`Remove "${product.name}"?`)) return;
        try {
            await api.delete(
                `/suppliers/${supplier.id}/recommended-products/${product.id}`);
            toast.success(`Removed "${product.name}"`);
            reload();
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("suppliers.failedToRemove", "Failed to remove")));
        }
    }

    return (
        <section className="mt-6" data-testid="recommended-products-section">
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm uppercase tracking-wide text-neutral-500">
                    {t("suppliers.recommendedProducts", "Recommended Products")}
                    {items && items.length > 0 && (
                        <span className="ml-2 text-neutral-400 normal-case tracking-normal">
                            ({items.length})
                        </span>
                    )}
                </h4>
                <button
                    type="button"
                    onClick={() => setEditing("new")}
                    data-testid="add-recommended-product-btn"
                    className="text-xs font-medium text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1 border border-neutral-300 rounded-md px-2.5 py-1 hover:bg-neutral-50"
                >
                    <Plus className="h-3.5 w-3.5" /> {t("suppliers.addProduct", "Add Product")}
                </button>
            </div>
            {loading && items === null ? (
                <div className="text-sm text-neutral-500 italic">{t("common.loading", "Loading…")}</div>
            ) : !items || items.length === 0 ? (
                <div className="text-sm text-neutral-500 italic"
                     data-testid="recommended-products-empty">
                    {t("suppliers.noRecommendedProductsYet", "No recommended products yet. Add one to remember what you usually buy from this supplier.")}
                </div>
            ) : (
                <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-md"
                    data-testid="recommended-products-list">
                    {items.map((p) => (
                        <li key={p.id}
                            data-testid={`recommended-product-${p.id}`}
                            className="flex items-start justify-between gap-3 px-3 py-2 hover:bg-neutral-50">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-neutral-900 truncate flex items-center gap-2">
                                    <span className="truncate">{p.name}</span>
                                    {p.scope === "company" && (
                                        <span
                                            data-testid={`recommended-product-shared-${p.id}`}
                                            className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 shrink-0"
                                            title={t("suppliers.sharedAcrossAllLocations", "Shared across all locations")}
                                        >
                                            {t("suppliers.shared", "Shared")}
                                        </span>
                                    )}
                                </div>
                                {p.note && (
                                    <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">
                                        {p.note}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setEditing(p)}
                                    data-testid={`recommended-product-edit-${p.id}`}
                                    className="p-1 text-neutral-500 hover:text-neutral-900"
                                    aria-label={t("common.edit", "Edit")}
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(p)}
                                    data-testid={`recommended-product-delete-${p.id}`}
                                    className="p-1 text-neutral-500 hover:text-red-600"
                                    aria-label={t("common.delete", "Delete")}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing && (
                <RecommendedProductDialog
                    supplierId={supplier.id}
                    product={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
        </section>
    );
}


function RecommendedProductDialog({ supplierId, product, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = !!product;
    const [name, setName] = useState(product?.name || "");
    const [note, setNote] = useState(product?.note || "");
    const [shared, setShared] = useState(product?.scope === "company");
    const [saving, setSaving] = useState(false);

    async function handleSave() {
        if (!name.trim()) {
            toast.error(t("suppliers.nameIsRequired", "Name is required"));
            return;
        }
        setSaving(true);
        try {
            const body = {
                name: name.trim(),
                note: note.trim(),
                scope: shared ? "company" : "location",
            };
            if (isEdit) {
                await api.put(
                    `/suppliers/${supplierId}/recommended-products/${product.id}`,
                    body);
                toast.success(`Updated "${body.name}"`);
            } else {
                await api.post(
                    `/suppliers/${supplierId}/recommended-products`, body);
                toast.success(`Added "${body.name}"`);
            }
            onSaved();
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("suppliers.failedToSave", "Failed to save")));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="recommended-product-dialog"
                            className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit ? t("suppliers.editProduct", "Edit Product") : t("suppliers.addRecommendedProduct", "Add Recommended Product")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("suppliers.visualReferenceHint", "Purely a visual reference for what you typically buy from this supplier. It does not affect purchasing, inventory, costs or reports.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <label className="text-xs font-medium text-neutral-500 uppercase">
                            Name<span className="text-red-500">*</span>
                        </label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("suppliers.eGChickenFish", "e.g. Chicken, Fish")}
                            className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                            data-testid="recommended-product-name"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-neutral-500 uppercase">
                            {t("suppliers.noteOptional", "Note (optional)")}
                        </label>
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={t("suppliers.eGOrderEveryMonday", "e.g. Order every Monday")}
                            className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                            data-testid="recommended-product-note"
                        />
                    </div>
                    <label className="flex items-start gap-2 text-sm pt-1 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={shared}
                            onChange={(e) => setShared(e.target.checked)}
                            data-testid="recommended-product-shared"
                            className="mt-0.5"
                        />
                        <span>
                            <span className="font-medium text-neutral-900">
                                {t("suppliers.shareAcrossAllLocations", "Share across all locations")}
                            </span>
                            <span className="block text-xs text-neutral-500 mt-0.5">
                                {t("suppliers.shareHint", "When on, every restaurant in your company sees this product. When off, only your restaurant sees it.")}
                            </span>
                        </span>
                    </label>
                </div>
                <DialogFooter className="mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="text-sm px-3 py-1.5 rounded-md border border-neutral-300 hover:bg-neutral-50"
                    >
                        {t("common.cancel", "Cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !name.trim()}
                        data-testid="recommended-product-save"
                        className="text-sm px-3 py-1.5 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50"
                    >
                        {saving ? t("common.saving", "Saving…") : isEdit ? t("common.saveChanges", "Save changes") : t("common.add", "Add")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


const EMPTY_SUPPLIER_FORM = {
    name: "", category: "", phone: "", email: "", website: "",
    address: "", contact_person: "", notes: "", is_recommended: false,
    vat_id: "", business_id: "",
};

function AddSupplierDialog({ open, onClose, onSaved }) {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const [form, setForm] = useState(EMPTY_SUPPLIER_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (open) setForm(EMPTY_SUPPLIER_FORM); }, [open]);

    const save = async () => {
        if (!form.name.trim()) { toast.error(t("suppliers.nameIsRequired", "Name is required")); return; }
        if (!form.category.trim()) { toast.error(t("suppliers.categoryIsRequired", "Category is required")); return; }
        setSaving(true);
        try {
            const body = { ...form };
            if (user.role === "corporate" && !body.is_recommended && restaurantId) {
                body.restaurant_id = restaurantId;
            }
            await api.post("/suppliers", body);
            toast.success(t("suppliers.supplierAdded", "Supplier added"));
            onSaved();
        } catch {
            toast.error(t("suppliers.couldNotSaveSupplier", "Could not save supplier"));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="add-supplier-dialog" className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t("suppliers.newSupplier", "New Supplier")}</DialogTitle>
                    <DialogDescription>{t("suppliers.addANewSupplierToYour", "Add a new supplier to your directory.")}</DialogDescription>
                </DialogHeader>
                <SupplierForm form={form} setForm={setForm} categories={DEFAULT_CATEGORIES} isCorporate={user.role === "corporate"} />
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                    <button data-testid="save-supplier-button" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditSupplierDialog({ supplier, onClose, onSaved, isCorporateUser }) {
    const { t } = useTranslation();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (supplier) {
            setForm({
                name: supplier.name || "",
                category: supplier.category || "",
                phone: supplier.phone || "",
                email: supplier.email || "",
                website: supplier.website || "",
                address: supplier.address || "",
                contact_person: supplier.contact_person || "",
                notes: supplier.notes || "",
                is_recommended: !!supplier.is_recommended,
                vat_id: supplier.vat_id || "",
                business_id: supplier.business_id || "",
            });
        }
    }, [supplier]);

    if (!supplier || !form) return null;

    const save = async () => {
        if (!form.name.trim()) { toast.error(t("suppliers.nameIsRequired", "Name is required")); return; }
        setSaving(true);
        try {
            const body = { ...form };
            if (!isCorporateUser) delete body.is_recommended;
            await api.patch(`/suppliers/${supplier.id}`, body);
            toast.success(t("suppliers.supplierUpdated", "Supplier updated"));
            onSaved();
        } catch (err) {
            toast.error((err?.response?.data?.detail != null ? apiErrorMessage(err) : t("suppliers.couldNotUpdateSupplier", "Could not update supplier")));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={!!supplier} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="edit-supplier-dialog" className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t("suppliers.editSupplier", "Edit Supplier")}</DialogTitle>
                    <DialogDescription>{t("suppliers.updateSupplierDetails", "Update supplier details.")}</DialogDescription>
                </DialogHeader>
                <SupplierForm form={form} setForm={setForm} categories={DEFAULT_CATEGORIES} isCorporate={isCorporateUser} />
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                    <button data-testid="update-supplier-button" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("common.saving", "Saving…") : t("common.saveChanges", "Save changes")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SupplierForm({ form, setForm, categories, isCorporate }) {
    const { t } = useTranslation();
    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    return (
        <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("suppliers.name", "Name *")}</label>
                <input data-testid="supplier-name" value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("suppliers.category", "Category *")}</label>
                <input data-testid="supplier-category" list="supplier-cats" value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                    placeholder={t("suppliers.eGFish", "e.g. Fish")}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
                <datalist id="supplier-cats">
                    {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("suppliers.contactPerson", "Contact person")}</label>
                <input data-testid="supplier-contact_person" value={form.contact_person}
                    onChange={(e) => set("contact_person", e.target.value)}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.phone", "Phone")}</label>
                <input data-testid="supplier-phone" value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.email", "Email")}</label>
                <input data-testid="supplier-email" value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("suppliers.website", "Website")}</label>
                <input data-testid="supplier-website" value={form.website}
                    onChange={(e) => set("website", e.target.value)}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("suppliers.vatId", "VAT ID")}</label>
                <input data-testid="supplier-vat_id" value={form.vat_id || ""}
                    onChange={(e) => set("vat_id", e.target.value)}
                    placeholder={t("suppliers.eGFi27099995", "e.g. FI27099995")}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("suppliers.businessId", "Business ID")}</label>
                <input data-testid="supplier-business_id" value={form.business_id || ""}
                    onChange={(e) => set("business_id", e.target.value)}
                    placeholder="e.g. 2709999-5"
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div className="col-span-2">
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.address", "Address")}</label>
                <input data-testid="supplier-address" value={form.address}
                    onChange={(e) => set("address", e.target.value)}
                    className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900" />
            </div>
            <div className="col-span-2">
                <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.notes", "Notes")}</label>
                <textarea data-testid="supplier-notes" rows={2} value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:border-neutral-900" />
            </div>
            {isCorporate && (
                <div className="col-span-2"
                     data-testid="supplier-visibility-picker">
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                        {t("suppliers.visibility", "Visibility")}
                    </label>
                    <div className="mt-1 flex gap-2">
                        {[
                            {v: false, label: t("suppliers.local", "Local"),     hint: t("suppliers.onlyThisRestaurant", "Only this restaurant")},
                            {v: true,  label: t("suppliers.corporate", "Corporate"), hint: t("suppliers.allRestaurantsInThisCompany", "All restaurants in this company")},
                        ].map(({v, label, hint}) => {
                            const active = !!form.is_recommended === v;
                            return (
                                <button type="button" key={label}
                                    data-testid={`supplier-visibility-${label.toLowerCase()}`}
                                    onClick={() => set("is_recommended", v)}
                                    className={`flex-1 p-3 rounded-md border text-left transition-colors ${
                                        active
                                            ? "bg-neutral-900 text-white border-neutral-900"
                                            : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
                                    }`}
                                >
                                    <div className="font-semibold text-sm">
                                        {active && "✓ "}{label}
                                    </div>
                                    <div className={`text-[11px] ${active ? "text-neutral-300" : "text-neutral-500"}`}>
                                        {hint}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
